<script setup lang="ts">
// Import logo assets
import logoLight from '~/assets/img/logo-light.png'
import logoDark from '~/assets/img/logo-dark.png'
import iconLight from '~/assets/img/icon-light.png'
import iconDark from '~/assets/img/icon-dark.png'

interface Props {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  // 'full' = wordmark, 'icon' = square mark (collapsed sidebar, tight spots).
  variant?: 'full' | 'icon'
  // Which surface the mark sits on. 'auto' follows the colour mode; 'dark'
  // pins the dark-surface asset for a panel that is dark in BOTH themes (the
  // auth brand panel), which the colour mode alone cannot express.
  surface?: 'auto' | 'dark'
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  variant: 'full',
  surface: 'auto',
  class: ''
})

const config = useRuntimeConfig()

// Size classes mapping
const sizeClasses = {
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-12',
  xl: 'h-16'
}

const logoClass = computed(() => {
  return `${sizeClasses[props.size]} ${props.class}`
})

// White-label overrides are all-or-nothing per variant: a half-configured pair
// would show the tenant's light asset over a dark background.
const customPair = computed(() => {
  const pair = props.variant === 'icon'
    ? [config.public.iconLightmodeUrl, config.public.iconDarkmodeUrl]
    : [config.public.logoLightmodeUrl, config.public.logoDarkmodeUrl]

  return pair[0]?.length && pair[1]?.length ? pair : null
})

const bundledPair = computed(() =>
  props.variant === 'icon' ? [iconLight, iconDark] : [logoLight, logoDark]
)

const pair = computed(() => customPair.value ?? bundledPair.value)

// The alt text is a brand name, so it comes from the same one rule the page
// title uses — never a literal, which is our name on a reseller's screen.
const { brandName } = useBranding()
</script>

<template>
  <!--
    Both assets are rendered and the `dark` class picks one, rather than one
    `<img>` behind `<ClientOnly>` reading `useColorMode()`.

    `<ClientOnly>` made the brand depend on hydration, and hydration is a single
    app-wide pass: a page whose `<script setup>` top-level-awaits its data (the
    checkout awaits catalog + datacenters + VPCs + subscriptions) holds the whole
    tree — sidebar included — on its SSR fallback until those resolve. The
    fallback carried a height class and no width, so the header sat blank for as
    long as that took instead of showing a skeleton. The colour-mode class is on
    <html> before first paint (the module's blocking inline script), so plain CSS
    resolves this at the right time and the logo ships in the server HTML.
  -->
  <img
    v-if="surface === 'dark'"
    :src="pair[1]"
    :alt="brandName"
    :class="logoClass"
    class="object-contain"
  >
  <template v-else>
    <img
      :src="pair[0]"
      :alt="brandName"
      :class="logoClass"
      class="object-contain dark:hidden"
    >
    <img
      :src="pair[1]"
      :alt="brandName"
      :class="logoClass"
      class="object-contain hidden dark:block"
    >
  </template>
</template>
